import {
    useEffect,
    useRef,
    useState,
    type ComponentProps,
    type FunctionComponent,
} from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Stack,
    Typography,
} from '@mui/material';
import { LAYOUT_VERTICAL_CHROME } from '@starjack/layout';
import { MarkdownInput } from '@starjack/shared-components';
import { ASSISTANT_API } from '@starjack/utils';
import { useNotification } from '@starjack/features/notifications';
import {
    CONFIG_FIELD_MAX_LENGTH,
    useGetAssistantConfigQuery,
    usePutAssistantConfigMutation,
    type AssistantConfigPayload,
} from '@starjack/entities/assistantConfig';
import {
    errorStatus,
    isConnectionError,
    MISCONFIGURED_MESSAGE,
} from '@/pageContents/kiEinstellungen/assistantApiError';

const EMPTY: AssistantConfigPayload = {
    behaviour: null,
    topicGuidance: null,
};

const LONG_TEXT_FIELDS = [
    {
        key: 'behaviour' as const,
        label: 'Wie soll sich der Assistent verhalten?',
        helper: 'Bitte in ganzen Sätzen beschreiben, z. B. Tonfall und Ansprache (förmlich/locker, Sie/Du).',
    },
];

// Support writes `#` to `###`. The prompt nests the text under
// `### Verhalten`, so the assistant has to shift these headings down.
const COMMANDS: ComponentProps<typeof MarkdownInput>['commands'] = [
    'heading',
    'bold',
    'italic',
];

// Every level between the card and the editor has to grow into the card's
// height and be allowed to shrink, or long text overflows the card instead of
// making the editor scroll.
const SHRINKING_COLUMN = {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minHeight: 0,
} as const;

export const AssistantBehaviourContent: FunctionComponent = () => {
    const { data, isLoading, error } = useGetAssistantConfigQuery(undefined, {
        skip: !ASSISTANT_API,
    });
    const [save, { isLoading: isSaving }] = usePutAssistantConfigMutation();
    const { addNotification } = useNotification();
    const [form, setForm] = useState<AssistantConfigPayload>(EMPTY);
    // Seed local state once on first load so a background refetch (another
    // tab saving, a cache invalidation) can't silently overwrite unsaved
    // keystrokes. A save this component performed re-seeds from the
    // mutation's own response instead.
    const seeded = useRef(false);

    useEffect(() => {
        if (data && !seeded.current) {
            setForm(data);
            seeded.current = true;
        }
    }, [data]);

    if (!ASSISTANT_API)
        return <Alert severity='error'>{MISCONFIGURED_MESSAGE}</Alert>;

    if (isLoading) return <CircularProgress />;
    if (error)
        return (
            <Alert severity='error'>
                {isConnectionError(error)
                    ? 'Assistant nicht erreichbar'
                    : 'Konfiguration nicht ladbar'}
            </Alert>
        );

    const update = (key: keyof AssistantConfigPayload, value: string) =>
        setForm((prev) => ({ ...prev, [key]: value === '' ? null : value }));

    const handleSave = async () => {
        try {
            // `topicGuidance` is hidden for now but still rendered into the
            // prompt if stored, so a save clears it rather than keeping
            // invisible text live.
            const saved = await save({ ...form, topicGuidance: null }).unwrap();
            // Re-sync with what the server actually persisted (e.g.
            // normalisation), never from a background fetch.
            setForm(saved);
            addNotification({ type: 'success', message: 'Gespeichert' });
        } catch (err) {
            addNotification({
                type: 'error',
                message:
                    errorStatus(err) === 400
                        ? 'Speichern fehlgeschlagen: ein Feld überschreitet die erlaubte Länge oder ein ungültiger Wert wurde gewählt.'
                        : 'Speichern fehlgeschlagen.',
            });
        }
    };

    return (
        <Card
            sx={{
                display: 'flex',
                flexDirection: 'column',
                // Always exactly one screen: minus the page's own `p: 3`
                // above and below, the editor fills the rest and scrolls.
                height: (theme) =>
                    `calc(100vh - ${LAYOUT_VERTICAL_CHROME}px - ${theme.spacing(6)})`,
            }}
        >
            <CardContent sx={SHRINKING_COLUMN}>
                <Stack spacing={3} sx={SHRINKING_COLUMN}>
                    <Typography variant='h6'>Verhalten</Typography>

                    {LONG_TEXT_FIELDS.map(({ key, label, helper }) => (
                        <Box key={key} sx={SHRINKING_COLUMN}>
                            <MarkdownInput
                                label={label}
                                value={form[key] ?? ''}
                                onChange={(value) => update(key, value)}
                                commands={COMMANDS}
                                autoGrow
                                // The server rejects anything longer; stopping
                                // the keystroke is friendlier than a 400 on save.
                                maxLength={CONFIG_FIELD_MAX_LENGTH}
                            />
                            <Typography
                                variant='caption'
                                color='text.secondary'
                            >
                                {`${helper} Max. ${CONFIG_FIELD_MAX_LENGTH} Zeichen.`}
                            </Typography>
                        </Box>
                    ))}

                    <Box>
                        <Button
                            variant='contained'
                            disabled={isSaving}
                            onClick={() => void handleSave()}
                        >
                            Speichern
                        </Button>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
};
